// PageEditor - make or change a custom tracking sheet (guide 19.1 / 19.7):
// a title and a sheet size, viewports that reference this view (plan or
// isometric, a level, zones, systems, and for a plan a crop dragged on the
// drawing), and the blocks around them. A default page is never edited
// here: "Copy to a new page" opens this editor with its content.
import React, { useMemo, useRef, useState } from "react";
import { Button, Checkbox, Input, Modal, Select, Space } from "antd";
import { BELOW, ROOF, levelName } from "./viewModel";
import { makeProjection, viewportRows } from "./SheetPages";
import { pointsAttr, sortedLevels } from "../glanceModel";

export const COLUMN_LABEL = { id: "ID", level: "Level", type: "Type", size: "Size", qty: "Qty", lengthFt: "Length", from: "From", to: "To", status: "Status" };
const SIZES = ["ARCH D", "ARCH E", "ANSI D", "ARCH C"];

// The blank page a "New page" starts from: one level in plan, its schedule.
export function blankPage(levelId) {
  return {
    title: "", size: "ARCH D",
    viewports: [{ projection: "plan", level: levelId || "" }],
    blocks: [{ type: "schedule", columns: ["id", "type", "size", "from", "to", "lengthFt", "status"] }, { type: "legend" }, { type: "title" }],
  };
}

// Plan pixels back to building feet (the inverse of the plan projector).
export function cropFromBox(manifest, vp, rows, width, box) {
  const proj = makeProjection(manifest, { ...vp, crop: undefined, projection: "plan" }, rows, width);
  const probe = [proj.P(0, 0), proj.P(1, 1)];
  const s = probe[1][0] - probe[0][0];
  if (!(s > 0)) return null;
  const toFt = ([px, py]) => [(px - probe[0][0]) / s, (probe[0][1] - py) / s];
  const a = toFt([box.x0, box.y1]); const b = toFt([box.x1, box.y0]);
  const w = b[0] - a[0]; const h = b[1] - a[1];
  if (!(w > 1 && h > 1)) return null;
  return [a[0], a[1], w, h].map((v) => Math.round(v * 10) / 10);
}

// The editor is a modal, rendered outside the glance page, so the page's colour
// variables do not reach it: the preview uses plain colours.
function CropPicker({ manifest, view, vp, onCrop }) {
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const width = 640;
  const rows = useMemo(() => viewportRows(view.rows, { ...vp, crop: undefined }), [view.rows, vp]);
  const proj = useMemo(() => makeProjection(manifest, { ...vp, crop: undefined, projection: "plan" }, rows, width), [manifest, vp, rows]);
  const toSvg = (e) => {
    const svg = svgRef.current; if (!svg || !svg.getScreenCTM) return [0, 0];
    const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    const m = svg.getScreenCTM(); if (!m) return [0, 0];
    const q = pt.matrixTransform(m.inverse()); return [q.x, q.y];
  };
  const crop = Array.isArray(vp.crop) && vp.crop.length === 4 ? vp.crop : null;
  const cropBox = crop ? [proj.P(crop[0], crop[1] + crop[3]), proj.P(crop[0] + crop[2], crop[1])] : null;
  return (
    <svg ref={svgRef} viewBox={`0 0 ${proj.width} ${proj.height}`} style={{ width: "100%", maxWidth: width, border: "1px solid #CBD5E1", background: "#F8FAFC", touchAction: "none", cursor: "crosshair" }}
         aria-label="Drag a box to crop this viewport"
         onPointerDown={(e) => { const p = toSvg(e); setDrag({ a: p, b: p }); if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId); }}
         onPointerMove={(e) => { if (drag) setDrag({ ...drag, b: toSvg(e) }); }}
         onPointerUp={() => {
           if (!drag) return;
           const box = { x0: Math.min(drag.a[0], drag.b[0]), x1: Math.max(drag.a[0], drag.b[0]), y0: Math.min(drag.a[1], drag.b[1]), y1: Math.max(drag.a[1], drag.b[1]) };
           setDrag(null);
           const c = cropFromBox(manifest, vp, rows, width, box);
           if (c) onCrop(c);
         }}>
      {proj.outline.map((pl) => <polygon key={pl.id} points={pointsAttr((pl.lvl && pl.lvl.footprint) || [], (p) => proj.P(p[0], p[1]))} fill="#FFFFFF" stroke="#1E293B" strokeWidth="1" strokeDasharray={pl.dashed ? "7 5" : undefined} />)}
      {rows.map((r) => <polyline key={r.id} points={pointsAttr(r.geom, (p) => proj.P(p[0], p[1]))} fill="none" stroke="#475569" strokeWidth="2" />)}
      {cropBox && <rect x={cropBox[0][0]} y={cropBox[0][1]} width={cropBox[1][0] - cropBox[0][0]} height={cropBox[1][1] - cropBox[0][1]} fill="rgba(31,111,209,.08)" stroke="#1F6FD1" />}
      {drag && <rect x={Math.min(drag.a[0], drag.b[0])} y={Math.min(drag.a[1], drag.b[1])} width={Math.abs(drag.b[0] - drag.a[0])} height={Math.abs(drag.b[1] - drag.a[1])} fill="none" stroke="#1F6FD1" strokeDasharray="4 3" />}
    </svg>
  );
}

export default function PageEditor({ open, manifest, view, initial, busy, onCancel, onSave }) {
  const [page, setPage] = useState(() => JSON.parse(JSON.stringify(initial)));
  const levels = sortedLevels(manifest);
  const levelOptions = [{ value: "", label: "Every level" }, ...(view.rows || []).some((r) => r.level === BELOW) ? [{ value: BELOW, label: levelName(manifest, BELOW) }] : [], ...levels.map((l) => ({ value: l.id, label: levelName(manifest, l.id) })), ...(view.rows || []).some((r) => r.level === ROOF) ? [{ value: ROOF, label: levelName(manifest, ROOF) }] : []];
  const zoneOptions = (manifest.zones || []).map((z) => ({ value: z.index, label: z.label }));
  const systemOptions = [...new Set((view.rows || []).map((r) => r.system).filter(Boolean))].map((s) => ({ value: s, label: s }));
  const setVp = (i, patch) => setPage((p) => ({ ...p, viewports: p.viewports.map((v, k) => (k === i ? { ...v, ...patch } : v)) }));
  const block = (type) => (page.blocks || []).find((b) => b.type === type);
  const toggleBlock = (type, on, extra = {}) => setPage((p) => ({ ...p, blocks: on ? [...(p.blocks || []).filter((b) => b.type !== type), { type, ...extra }] : (p.blocks || []).filter((b) => b.type !== type) }));
  const schedule = block("schedule");
  const notes = block("notes");
  const valid = page.title.trim() && ((page.viewports || []).length || schedule);
  return (
    <Modal open={open} title={initial.pageId ? `Edit "${initial.title}"` : "New tracking sheet"} width={760} okText="Save page" confirmLoading={busy} maskClosable={false}
           okButtonProps={{ disabled: !valid }} onCancel={onCancel} onOk={() => onSave(page)}>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Space wrap>
          <Input id="page-title" placeholder="Page title, e.g. L2 west sleeves" value={page.title} style={{ width: 360 }} onChange={(e) => setPage({ ...page, title: e.target.value })} />
          <Select value={page.size || "ARCH D"} style={{ width: 120 }} options={SIZES.map((s) => ({ value: s, label: s }))} onChange={(size) => setPage({ ...page, size })} />
        </Space>
        {(page.viewports || []).map((vp, i) => (
          <div key={i} className="g-sheet-editvp">
            <Space wrap>
              <b>Drawing {i + 1}</b>
              <Select size="small" value={vp.projection || "iso"} style={{ width: 120 }} options={[{ value: "plan", label: "Plan" }, { value: "iso", label: "Isometric" }]} onChange={(projection) => setVp(i, { projection, crop: projection === "plan" ? vp.crop : undefined })} />
              <Select size="small" value={vp.level || ""} style={{ width: 150 }} options={levelOptions} onChange={(level) => setVp(i, { level, crop: undefined })} />
              <Select size="small" mode="multiple" allowClear placeholder="Every zone" value={vp.zones || []} style={{ minWidth: 180 }} options={zoneOptions} onChange={(zones) => setVp(i, { zones })} />
              <Select size="small" mode="multiple" allowClear placeholder="Every system" value={vp.systems || []} style={{ minWidth: 160 }} options={systemOptions} onChange={(systems) => setVp(i, { systems })} />
              <Button size="small" danger type="text" onClick={() => setPage((p) => ({ ...p, viewports: p.viewports.filter((_, k) => k !== i) }))}>Remove</Button>
            </Space>
            {vp.projection === "plan" && vp.level && (
              <div className="g-sheet-crop">
                <div className="g-tsub">Drag a box on the plan to crop this drawing{vp.crop ? "" : " (whole level now)"}.
                  {vp.crop && <Button size="small" type="link" onClick={() => setVp(i, { crop: undefined })}>Clear crop</Button>}
                </div>
                <CropPicker manifest={manifest} view={view} vp={vp} onCrop={(crop) => setVp(i, { crop })} />
              </div>
            )}
          </div>
        ))}
        {(page.viewports || []).length < 6 && (
          <Button size="small" onClick={() => setPage((p) => ({ ...p, viewports: [...(p.viewports || []), { projection: "plan", level: (levels[0] || {}).id || "" }] }))}>Add a drawing</Button>
        )}
        <div>
          <Checkbox checked={!!schedule} onChange={(e) => toggleBlock("schedule", e.target.checked, { columns: ["id", "type", "size", "from", "to", "lengthFt", "status"] })}>Schedule</Checkbox>
          {schedule && (
            <Select size="small" mode="multiple" value={schedule.columns || []} style={{ minWidth: 360, marginLeft: 8 }}
                    options={Object.entries(COLUMN_LABEL).map(([value, label]) => ({ value, label }))}
                    onChange={(columns) => toggleBlock("schedule", true, { columns })} />
          )}
        </div>
        <Space wrap>
          <Checkbox checked={!!block("legend")} onChange={(e) => toggleBlock("legend", e.target.checked)}>Status legend and counts</Checkbox>
          <Checkbox checked={!!block("title")} onChange={(e) => toggleBlock("title", e.target.checked)}>Title block</Checkbox>
          <Checkbox checked={!!notes} onChange={(e) => toggleBlock("notes", e.target.checked, { text: "" })}>Notes</Checkbox>
        </Space>
        {notes && <Input.TextArea id="page-notes" rows={3} value={notes.text} placeholder="Notes printed on the page" onChange={(e) => toggleBlock("notes", true, { text: e.target.value })} />}
      </Space>
    </Modal>
  );
}
